import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api, tokenStorage, ApiError } from '@/services/api';
import type {
  User,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  AuthTokens,
  UpdateProfileData,
  ChangePasswordData,
} from '@/types/user.types';
import { AUTH_CONFIG } from '@/utils/constants';

// Auth state interface
interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

// Initial state
const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,
};

// Helper to save user to localStorage
const saveUserToStorage = (user: User): void => {
  localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(user));
};

// Helper to get user from localStorage
const getUserFromStorage = (): User | null => {
  const userJson = localStorage.getItem(AUTH_CONFIG.USER_KEY);
  if (userJson) {
    try {
      return JSON.parse(userJson) as User;
    } catch {
      return null;
    }
  }
  return null;
};

// Async thunks
export const login = createAsyncThunk<AuthResponse, LoginCredentials, { rejectValue: string }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/login', credentials);
      const data = response.data.data; // Extract from wrapper

      // Store tokens
      tokenStorage.setTokens(data.tokens.accessToken, data.tokens.refreshToken);
      saveUserToStorage(data.user);

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const register = createAsyncThunk<AuthResponse, RegisterData, { rejectValue: string }>(
  'auth/register',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/register', data);
      const responseData = response.data.data; // Extract from wrapper

      // Store tokens
      tokenStorage.setTokens(responseData.tokens.accessToken, responseData.tokens.refreshToken);
      saveUserToStorage(responseData.user);

      return responseData;
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const logout = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      // Call logout endpoint to invalidate token on server
      await api.post('/auth/logout');
    } catch (error) {
      // Even if the API call fails, we still want to clear local state
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local tokens
      tokenStorage.clearTokens();
    }
  }
);

export const fetchCurrentUser = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<{ data: User }>('/auth/me');
      const user = response.data.data;
      saveUserToStorage(user);
      return user;
    } catch (error) {
      tokenStorage.clearTokens();
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const updateProfile = createAsyncThunk<User, UpdateProfileData, { rejectValue: string }>(
  'auth/updateProfile',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.patch<{ data: User }>('/auth/profile', data);
      const user = response.data.data;
      saveUserToStorage(user);
      return user;
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const changePassword = createAsyncThunk<void, ChangePasswordData, { rejectValue: string }>(
  'auth/changePassword',
  async (data, { rejectWithValue }) => {
    try {
      await api.post('/auth/change-password', data);
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const initializeAuth = createAsyncThunk<User | null, void, { rejectValue: string }>(
  'auth/initialize',
  async (_, { dispatch, rejectWithValue }) => {
    console.log('BOIS Auth: initializeAuth started');
    try {
      const token = tokenStorage.getAccessToken();
      console.log('BOIS Auth: Token exists?', !!token);

      if (!token) {
        console.log('BOIS Auth: No token, returning null');
        return null;
      }

      // Try to get user from localStorage first for faster initial render
      const cachedUser = getUserFromStorage();

      // Fetch fresh user data from API
      try {
        const result = await dispatch(fetchCurrentUser()).unwrap();
        return result;
      } catch {
        // If API call fails but we have cached user, use that temporarily
        if (cachedUser) {
          return cachedUser;
        }
        throw new Error('Session expired');
      }
    } catch (error) {
      tokenStorage.clearTokens();
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to initialize auth');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set credentials manually (for OAuth callbacks, etc.)
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; tokens: AuthTokens }>
    ) => {
      state.user = action.payload.user;
      state.tokens = action.payload.tokens;
      state.isAuthenticated = true;
      state.error = null;

      tokenStorage.setTokens(
        action.payload.tokens.accessToken,
        action.payload.tokens.refreshToken
      );
      saveUserToStorage(action.payload.user);
    },

    // Update user in state
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        saveUserToStorage(state.user);
      }
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Reset auth state
    resetAuth: (state) => {
      state.user = null;
      state.tokens = null;
      state.isAuthenticated = false;
      state.error = null;
      tokenStorage.clearTokens();
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Login failed';
      });

    // Register
    builder
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Registration failed';
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        // Even on rejection, clear auth state
        state.isLoading = false;
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
      });

    // Fetch Current User
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.error = action.payload ?? 'Failed to fetch user';
      });

    // Update Profile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to update profile';
      });

    // Change Password
    builder
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to change password';
      });

    // Initialize Auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        console.log('BOIS Auth: initializeAuth.pending');
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        console.log('BOIS Auth: initializeAuth.fulfilled', { hasUser: !!action.payload });
        state.isLoading = false;
        state.isInitialized = true;
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        console.log('BOIS Auth: initializeAuth.rejected');
        state.isLoading = false;
        state.isInitialized = true;
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
      });
  },
});

// Export actions
export const { setCredentials, updateUser, clearError, resetAuth } = authSlice.actions;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectIsLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectIsInitialized = (state: { auth: AuthState }) => state.auth.isInitialized;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;

// Export reducer
export default authSlice.reducer;
